import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Banner, Button, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import type {
  JobLeaveFileSigner,
  JobLeavePendingSignature,
} from "types/JobLeave";

interface JobLeaveRemoveSignatureModalProps {
  signature: JobLeavePendingSignature;
  onConfirm: () => Promise<void>;
}

function statusToneClass(status: string) {
  const s = (status || "").toLowerCase();
  if (s === "signed") return "text-green-700 bg-green-50";
  if (s === "pending") return "text-amber-800 bg-amber-50";
  if (s === "rejected") return "text-red-700 bg-red-50";
  return "text-gray-700 bg-gray-100";
}

function computeOutcome(
  signType: string | null | undefined,
  signers: JobLeaveFileSigner[],
  removeId: string
) {
  const type = signType || "Click";
  if (type === "Signontab") {
    return "signontab_clear_all" as const;
  }
  const remaining = signers.filter((s) => s.id !== removeId);
  if (!remaining.length) {
    return "only_signer_removed" as const;
  }
  if (remaining.every((s) => (s.status || "").toLowerCase() === "signed")) {
    return "will_fully_sign" as const;
  }
  return "remove_only" as const;
}

export default function JobLeaveRemoveSignatureModal({
  signature,
  onConfirm,
}: JobLeaveRemoveSignatureModalProps) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const signers = signature.fileSigners || [];
  const signType = signature.file?.signType || "Click";
  const target =
    signers.find((s) => s.id === signature.id) ||
    signers.find((s) => s.isTarget);
  const targetName =
    target?.user?.name ||
    target?.user?.email ||
    t("this_signer");

  const outcome = useMemo(
    () => computeOutcome(signType, signers, signature.id),
    [signType, signers, signature.id]
  );

  const outcomeMessage = useMemo(() => {
    if (!signers.length) {
      return t("job_leave_remove_signer_outcome_unknown_signers", {
        name: targetName,
        signType,
      });
    }
    switch (outcome) {
      case "signontab_clear_all":
        return t("job_leave_remove_signer_outcome_signontab");
      case "will_fully_sign":
        return t("job_leave_remove_signer_outcome_fully_signed", {
          name: targetName,
          signType,
        });
      case "only_signer_removed":
        return t("job_leave_remove_signer_outcome_only_signer", {
          name: targetName,
        });
      default:
        return t("job_leave_remove_signer_outcome_remove_only", {
          name: targetName,
        });
    }
  }, [outcome, targetName, signType, signers.length, t]);

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      setErrMsg(null);
      await onConfirm();
      closeModal();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || t("error");
      setErrMsg(typeof msg === "string" ? msg : t("error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {errMsg && (
        <Banner tone="critical" onDismiss={() => setErrMsg(null)}>
          {errMsg}
        </Banner>
      )}

      <div className="space-y-1">
        <Text as="p" variant="bodySm" tone="subdued">
          {t("document")}
        </Text>
        <Text as="p" variant="bodyMd" fontWeight="semibold">
          {signature.file?.title || signature.id}
        </Text>
      </div>

      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 flex items-center justify-between gap-2">
        <Text as="span" variant="bodySm" tone="subdued">
          {t("sign_type")}
        </Text>
        <Text as="span" variant="bodyMd" fontWeight="semibold">
          {signType}
        </Text>
      </div>

      <div>
        <Text as="h3" variant="headingSm">
          {t("signers")}
        </Text>
        {!signers.length ? (
          <p className="text-sm text-gray-500 mt-2">{t("no_signers")}</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 border border-gray-200 rounded-md overflow-hidden">
            {signers.map((s) => {
              const name = s.user?.name || s.user?.email || s.user?.id || "—";
              const isRemoving = s.id === signature.id || s.isTarget;
              return (
                <li
                  key={s.id}
                  className={`px-3 py-2 flex items-center justify-between gap-2 ${
                    isRemoving ? "bg-red-50/60" : "bg-white"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {name}
                      {isRemoving ? (
                        <span className="ml-2 text-xs font-normal text-red-700">
                          ({t("will_be_removed")})
                        </span>
                      ) : null}
                    </div>
                    {s.user?.email && s.user?.name ? (
                      <div className="text-xs text-gray-500 truncate">
                        {s.user.email}
                      </div>
                    ) : null}
                    {s.position ? (
                      <div className="text-xs text-gray-500">{s.position}</div>
                    ) : null}
                  </div>
                  <span
                    className={`shrink-0 text-xs px-2 py-0.5 rounded ${statusToneClass(
                      s.status
                    )}`}
                  >
                    {t(s.status) !== s.status ? t(s.status) : s.status}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Banner tone={outcome === "will_fully_sign" ? "success" : "warning"}>
        {outcomeMessage}
      </Banner>

      <div className="flex justify-end gap-3 border-t border-gray-150 pt-4">
        <Button onClick={closeModal} disabled={isLoading}>
          {t("No")}
        </Button>
        <Button
          onClick={handleConfirm}
          loading={isLoading}
          variant="primary"
          tone="critical"
        >
          {t("remove_signature")}
        </Button>
      </div>
    </div>
  );
}
